/**
 * Pantalla inicial: verificar licencia → activar si falta → luego permitir login y app.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { VpnKey, ContentCopy } from '@mui/icons-material';
import axios from 'axios';
import { API_URL, getApiUrlDisplay } from '../config';
import type { LicenseStatus } from '../contexts/SystemContext';

type Phase = 'checking' | 'activate' | 'ok';

const LICENSE_CHECK_TIMEOUT_MS = 6_000;
const CHECK_STUCK_MS = 8_000;

/** Si el gateway :3001 no responde, intenta el API directo en :5000 (misma máquina). */
function licenseApiBases(): string[] {
  const bases = [API_URL.replace(/\/+$/, '')];
  if (typeof window !== 'undefined' && window.location.port === '3001') {
    const direct = `${window.location.protocol}//${window.location.hostname}:5000/api`;
    if (!bases.includes(direct)) bases.push(direct);
  }
  return bases;
}

async function getLicenseJson<T>(path: string): Promise<T> {
  let lastErr: unknown;
  for (const base of licenseApiBases()) {
    try {
      const res = await axios.get(`${base}${path}`, { timeout: LICENSE_CHECK_TIMEOUT_MS });
      return res.data as T;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr;
}

async function postLicenseJson<T>(path: string, body: unknown): Promise<T> {
  let lastErr: unknown;
  for (const base of licenseApiBases()) {
    try {
      const res = await axios.post(`${base}${path}`, body, { timeout: LICENSE_CHECK_TIMEOUT_MS });
      return res.data as T;
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr;
}

const LicenseBootstrap: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('checking');
  const [license, setLicense] = useState<LicenseStatus | null>(null);
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activating, setActivating] = useState(false);
  const licenseOkRef = useRef(false);
  const initialCheckDoneRef = useRef(false);
  const checkInFlightRef = useRef(false);

  const checkLicense = useCallback(async (options?: { force?: boolean }) => {
    if (licenseOkRef.current && !options?.force) return;
    if (checkInFlightRef.current && !options?.force) return;

    checkInFlightRef.current = true;
    if (!licenseOkRef.current || options?.force) {
      setPhase('checking');
    }
    setError(null);
    try {
      const [data, machine] = await Promise.all([
        getLicenseJson<LicenseStatus>('/licensing/status'),
        getLicenseJson<{ machineId: string }>('/licensing/machine-id').catch(() => ({
          machineId: '—',
        })),
      ]);
      if (!data.licensingEnabled || data.valid) {
        licenseOkRef.current = true;
        setPhase('ok');
        return;
      }
      licenseOkRef.current = false;
      setLicense({
        ...data,
        machineId: data.machineId || machine.machineId || '—',
      });
      setPhase('activate');
    } catch (e: unknown) {
      licenseOkRef.current = false;
      const err = e as { code?: string; message?: string; response?: { status?: number; data?: { error?: string } } };
      const isTimeout = err.code === 'ECONNABORTED';
      const apiHint = getApiUrlDisplay();
      const backendHint =
        typeof window !== 'undefined' && window.location.port === '3001'
          ? ' Compruebe en PM2 que «Gestion-Lab-API» esté online (puerto 5000): pm2 list && pm2 restart Gestion-Lab-API'
          : '';
      setError(
        isTimeout
          ? `Tiempo de espera agotado al contactar ${apiHint}.${backendHint} Si usa desarrollo: backend en :5000 y frontend en :3000 con npm run dev.`
          : err.response?.data?.error ||
              `No se pudo contactar al servidor (${apiHint}).${backendHint}`
      );
      setPhase('activate');
      try {
        const mid = await getLicenseJson<{ machineId: string }>('/licensing/machine-id');
        setLicense({
          licensingEnabled: true,
          valid: false,
          machineId: mid.machineId || '—',
          message: 'Sin conexión al verificar licencia',
        });
      } catch {
        setLicense({
          licensingEnabled: true,
          valid: false,
          machineId: '—',
          message: 'Sin conexión',
        });
      }
    } finally {
      checkInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (location.pathname === '/generador-licencias') {
      licenseOkRef.current = true;
      setPhase('ok');
      return;
    }
    if (initialCheckDoneRef.current) return;
    initialCheckDoneRef.current = true;
    void checkLicense();
  }, [checkLicense, location.pathname]);

  useEffect(() => {
    if (phase !== 'checking') return;
    const t = window.setTimeout(() => {
      if (!licenseOkRef.current) {
        checkInFlightRef.current = false;
        setError(
          (prev) =>
            prev ||
            `La verificación tardó demasiado (${getApiUrlDisplay()}). Pulse «Reintentar» o revise el túnel/proxy.`
        );
        setPhase('activate');
        setLicense((prev) =>
          prev ?? {
            licensingEnabled: true,
            valid: false,
            machineId: '—',
            message: 'Verificación bloqueada',
          }
        );
      }
    }, CHECK_STUCK_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  const handleActivate = async () => {
    if (!key.trim()) return;
    setActivating(true);
    setError(null);
    try {
      const data = await postLicenseJson<LicenseStatus>('/licensing/activate', {
        license_key: key.trim(),
      });
      if (data.valid) {
        licenseOkRef.current = true;
        setPhase('ok');
        navigate('/login', { replace: true });
      } else {
        setError(data.message || 'Licencia no válida');
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'No se pudo activar la licencia');
    } finally {
      setActivating(false);
    }
  };

  if (phase === 'checking') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          gap: 2,
          p: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" color="text.secondary">
          Verificando licencia…
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', textAlign: 'center' }}>
          API: {getApiUrlDisplay()}
        </Typography>
        <Button size="small" variant="outlined" onClick={() => void checkLicense({ force: true })}>
          Reintentar
        </Button>
      </Box>
    );
  }

  if (phase === 'activate') {
    const machineId = license?.machineId || '';
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Paper elevation={4} sx={{ p: 4, maxWidth: 560, width: '100%' }}>
          <Typography variant="h5" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VpnKey color="primary" />
            Activación de licencia
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Este sistema requiere una licencia válida para este equipo. Envíe el identificador siguiente al proveedor
            para obtener su clave de activación.
          </Typography>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            ID de esta máquina
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography
              component="code"
              sx={{ fontFamily: 'monospace', fontSize: '0.95rem', wordBreak: 'break-all', flex: 1 }}
            >
              {machineId}
            </Typography>
            {machineId && machineId !== '—' && (
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={() => navigator.clipboard.writeText(machineId)}
              >
                Copiar
              </Button>
            )}
          </Paper>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Clave de licencia
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            placeholder="Pegue aquí la licencia que le envió el proveedor"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            sx={{ mb: 2 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleActivate}
            disabled={activating || !key.trim()}
          >
            {activating ? 'Activando…' : 'Activar y continuar'}
          </Button>
          <Button
            size="small"
            fullWidth
            sx={{ mt: 1 }}
            onClick={() => void checkLicense({ force: true })}
            disabled={activating}
          >
            Volver a verificar conexión
          </Button>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
};

export default LicenseBootstrap;
